import { BitkitAlert, BitkitButton, BitkitFormDialog, BitkitTextArea } from '@bitrise/bitkit-v2';
import { Text } from '@chakra-ui/react/text';
import { useForm } from 'react-hook-form';

type ConfirmAbortBuildDialogProps = {
  isOpen: boolean;
  type: 'build' | 'pipeline';
  error?: string;
  onClose: () => void;
  onConfirm: (abortReason: string) => void;
  isLoading?: boolean;
};

const texts = {
  build: {
    title: 'Abort this Build?',
    content: 'Are you sure you want to abort this build?',
    confirmButton: 'Abort Build',
  },
  pipeline: {
    title: 'Abort this Pipeline?',
    content: 'Are you sure you want to abort this pipeline?',
    confirmButton: 'Abort Pipeline',
  },
};

const ConfirmAbortBuildDialog = ({
  isOpen,
  type,
  error,
  onClose,
  onConfirm,
  isLoading,
}: ConfirmAbortBuildDialogProps) => {
  const { handleSubmit, register, reset } = useForm();

  const { ref: abortReasonRef, ...abortReasonProps } = register('abortReason');

  const onFormSubmit = handleSubmit((values) => {
    onConfirm(values.abortReason);
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <BitkitFormDialog
      title={`${texts[type].title}`}
      open={isOpen}
      onOpenChange={({ open }) => !open && handleClose()}
      onSubmit={onFormSubmit}
      footerContent={error ? <BitkitAlert variant="critical" messageText={error} /> : undefined}
      footerButtons={
        <>
          <BitkitButton variant="secondary" onClick={handleClose} state={isLoading ? 'disabled' : undefined}>
            Cancel
          </BitkitButton>
          <BitkitButton type="submit" state={isLoading ? 'loading' : undefined}>
            {texts[type].confirmButton}
          </BitkitButton>
        </>
      }
    >
      <Text>{texts[type].content}</Text>
      <BitkitTextArea
        ref={abortReasonRef}
        label="Abort reason"
        helperText="Optional. The reason will be visible on the Build Details page, and the email notifications."
        optional
        state={isLoading ? 'disabled' : error ? 'error' : undefined}
        textareaProps={abortReasonProps}
      />
    </BitkitFormDialog>
  );
};

export default ConfirmAbortBuildDialog;
